import { z } from "zod";
export const TEMPLATE_VERSION = "AI Link Invoice Template v4 — PDF 1";
export const kinds = ["invoice", "proFormaInvoice", "quotation"] as const;
export type Kind = (typeof kinds)[number];
export type Status =
  "draft" | "issuing" | "issued" | "paid" | "void" | "deleted";
export const kindLabels: Record<Kind, string> = {
  invoice: "Invoice",
  proFormaInvoice: "Pro forma",
  quotation: "Quotation",
};
export const kindPrefixes: Record<Kind, string> = {
  invoice: "ALL",
  proFormaInvoice: "ALP",
  quotation: "ALQ",
};
const text = (max: number) => z.string().trim().max(max);
export function isDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const d = new Date(`${value}T12:00:00Z`);
  return (
    Number.isFinite(d.valueOf()) &&
    d.toISOString().slice(0, 10) === value &&
    value >= "2000-01-01" &&
    value <= "2100-12-31"
  );
}
export const dateSchema = z
  .string()
  .refine(isDate, "Enter a valid calendar date.");
export const addressSchema = z.object({
  line1: text(150),
  line2: text(150).default(""),
  city: text(100),
  postcode: text(20),
  country: text(80).default("United Kingdom"),
});
export const clientSchema = z.object({
  id: z.string().regex(/^[A-Za-z0-9_-]{1,80}$/, "Use a valid record ID."),
  businessName: text(150).min(1, "Enter the customer’s business name."),
  billingAddress: addressSchema,
  contactName: text(120).default(""),
  email: z.union([z.literal(""), z.email()]).default(""),
  phone: text(40).default(""),
  vatRegistrationNumber: text(40).default(""),
  defaultPaymentTermDays: z.number().int().min(0).max(365).default(1),
  defaultServiceDescription: text(500).default("Logistics services"),
  version: z.number().int().positive().default(1),
});
export type Client = z.infer<typeof clientSchema>;
export const profileSchema = z.object({
  legalName: text(150).min(1),
  tradingName: text(150),
  companyNumber: text(30).min(1),
  vatRegistrationNumber: text(40),
  invoiceAddress: addressSchema,
  email: z.email(),
  telephone: text(40).min(1),
  website: text(150),
  paymentDetails: z.object({
    accountName: text(150).min(1),
    accountNumber: z
      .string()
      .regex(/^\d{8}$/, "Enter an 8-digit account number."),
    sortCode: z
      .string()
      .regex(/^\d{2}-?\d{2}-?\d{2}$/, "Enter a UK sort code."),
  }),
  defaultVatBasisPoints: z.number().int().min(0).max(10000).default(2000),
});
export type Profile = z.infer<typeof profileSchema>;
export const lineSchema = z.object({
  id: z.string().regex(/^[A-Za-z0-9_-]{1,80}$/, "Use a valid record ID."),
  description: text(600),
  supplyDate: z.string().max(10),
  netPence: z.number().int().min(0).max(100000000),
});
export type Line = z.infer<typeof lineSchema>;
export const draftSchema = z.object({
  id: z.string().regex(/^[A-Za-z0-9_-]{1,80}$/, "Use a valid record ID."),
  kind: z.enum(kinds),
  clientId: z.string().max(80),
  issueDate: z.string().max(10),
  dueDate: z.string().max(10),
  lines: z
    .array(lineSchema)
    .min(1)
    .max(30)
    .refine(
      (lines) => new Set(lines.map((l) => l.id)).size === lines.length,
      "Each service must have a unique ID.",
    ),
  vatBasisPoints: z.number().int().min(0).max(10000),
  customerReference: text(150).default(""),
  internalNote: text(2000).default(""),
  amountPaidPence: z.number().int().min(0).max(100000000).default(0),
  paymentReceivedDate: z.string().max(10).default(""),
  version: z.number().int().min(0),
});
export type Draft = z.infer<typeof draftSchema>;
export type Snapshot = {
  draft: Draft;
  client: Client;
  business: Profile;
  number: string;
  templateVersion: string;
};
export interface DocumentRecord {
  id: string;
  kind: Kind;
  status: Status;
  number: string | null;
  clientName: string;
  issueDate: string;
  dueDate: string;
  totalPence: number;
  paidPence: number;
  paymentDate: string | null;
  version: number;
  createdAt: string;
  updatedAt: string;
  draft: Draft;
  snapshot: Snapshot | null;
  pdfReady: boolean;
  replacementId: string | null;
}
export interface Bootstrap {
  user: { email: string; local: boolean };
  business: Profile;
  businessVersion: number;
  clients: Client[];
  documents: DocumentRecord[];
  hasMore: boolean;
  stats: { outstandingPence: number; overdueCount: number; draftCount: number };
  today: string;
}
export function totals(
  d: Pick<Draft, "lines" | "vatBasisPoints" | "amountPaidPence">,
) {
  const netPence = d.lines.reduce((s, l) => s + l.netPence, 0);
  const vatPence = Number(
    (BigInt(netPence) * BigInt(d.vatBasisPoints) + 5000n) / 10000n,
  );
  const totalPence = netPence + vatPence;
  return {
    netPence,
    vatPence,
    totalPence,
    balancePence: totalPence - d.amountPaidPence,
  };
}
export function money(pence: number) {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
  }).format(pence / 100);
}
export function parseMoney(value: string): number | null {
  if (!/^\d{1,7}(\.\d{0,2})?$/.test(value.trim())) return null;
  const [p, f = ""] = value.trim().split(".");
  return Number(p) * 100 + Number(f.padEnd(2, "0"));
}
export function londonToday() {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/London",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}
export function addDays(value: string, days: number) {
  const d = new Date(`${value}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}
export function dateLabel(value: string, short = false) {
  if (!isDate(value)) return value;
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: short ? "short" : "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(`${value}T12:00:00Z`));
}
export function addressLines(a: Profile["invoiceAddress"]) {
  return [a.line1, a.line2, a.city, a.postcode, a.country].filter(Boolean);
}
export function newDraft(vat = 2000): Draft {
  const today = londonToday();
  return {
    id: crypto.randomUUID(),
    kind: "invoice",
    clientId: "",
    issueDate: today,
    dueDate: addDays(today, 1),
    lines: [
      {
        id: crypto.randomUUID(),
        description: "",
        supplyDate: today,
        netPence: 0,
      },
    ],
    vatBasisPoints: vat,
    customerReference: "",
    internalNote: "",
    amountPaidPence: 0,
    paymentReceivedDate: "",
    version: 0,
  };
}
export function validationErrors(
  d: Draft,
  client?: Client,
): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!client) errors.clientId = "Choose a customer.";
  else if (!client.billingAddress.line1 || !client.billingAddress.postcode)
    errors.clientId = "Add the customer’s billing address and postcode first.";
  if (!isDate(d.issueDate)) errors.issueDate = "Enter a valid issue date.";
  if (!isDate(d.dueDate) || d.dueDate < d.issueDate)
    errors.dueDate = "The due date must be on or after the issue date.";
  d.lines.forEach((l, i) => {
    if (!l.description.trim())
      errors[`line.${i}.description`] = "Describe this service.";
    if (!isDate(l.supplyDate))
      errors[`line.${i}.supplyDate`] = "Enter a valid service date.";
    if (!Number.isSafeInteger(l.netPence) || l.netPence <= 0)
      errors[`line.${i}.netPence`] = "Enter an amount above £0.00.";
  });
  if (d.amountPaidPence > totals(d).totalPence)
    errors.amountPaidPence = "Payment cannot exceed the invoice total.";
  if (
    d.amountPaidPence > 0 &&
    (!isDate(d.paymentReceivedDate) || d.paymentReceivedDate > londonToday())
  )
    errors.paymentReceivedDate =
      "Enter a valid payment date that is not in the future.";
  if (d.kind !== "invoice" && d.amountPaidPence > 0)
    errors.amountPaidPence = "Record received payments on an invoice.";
  return errors;
}
export function statusLabel(doc: DocumentRecord, today = londonToday()) {
  if (doc.status === "issued" && doc.kind === "invoice")
    return doc.dueDate < today ? "Overdue" : "Unpaid";
  return {
    draft: "Draft",
    issuing: "Finishing issuance",
    issued: "Issued",
    paid: "Paid",
    void: "Void",
    deleted: "Deleted",
  }[doc.status];
}
